import type { InventoryOperation, InventoryRow, SortDirection } from '../AppAndSiteMarketplace.types';

/** Keeps the items containing `searchText` (case insensitive). Returns the same array when there is no search. */
export const filterBySearch = (items: string[], searchText?: string): string[] => {
  if (!searchText) {
    return items;
  }
  const lowerSearch = searchText.toLowerCase();
  return items.filter(item => item.toLowerCase().includes(lowerSearch));
};

/** Keeps the rows whose label contains `searchText` (case insensitive). Returns the same array when there is no search. */
export const filterRowsBySearch = (rows: InventoryRow[], searchText?: string): InventoryRow[] => {
  if (!searchText) {
    return rows;
  }
  const lowerSearch = searchText.toLowerCase();
  return rows.filter(row => row.label.toLowerCase().includes(lowerSearch));
};

/** Returns a copy of the rows sorted by label. Without direction the insertion order is kept. */
export const sortRows = (rows: InventoryRow[], direction?: SortDirection): InventoryRow[] => {
  if (!direction) {
    return rows;
  }
  const factor = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => factor * a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
};

/** Selection keys are qualified by column because the same item can be in both lists. */
export const getSelectionKey = (operation: InventoryOperation, id: string): string => `${operation}:${id}`;

/** Ids selected in one column. */
export const getSelectedIds = (selectedList: string[], operation: InventoryOperation): string[] => {
  const prefix = `${operation}:`;
  return selectedList.filter(key => key.startsWith(prefix)).map(key => key.slice(prefix.length));
};

/** Keeps the selection keys whose id contains `searchText` (case insensitive). */
export const filterSelectionBySearch = (selectedList: string[], searchText?: string): string[] =>
  selectedList.filter(key => filterBySearch([key.slice(key.indexOf(':') + 1)], searchText).length > 0);

/** Items present in both lists (exclusions take priority over inclusions). */
export const getConflictingItems = (included: string[], excluded: string[]): Set<string> => {
  const excludedSet = new Set(excluded);
  return new Set(included.filter(item => excludedSet.has(item)));
};

/** Replaces the selection of one column (`columnKeys`) keeping the selection of the rest of keys. */
export const mergeColumnSelection = (
  selectedList: string[],
  columnKeys: string[],
  columnSelection: string[]
): string[] => {
  const columnKeysSet = new Set(columnKeys);
  return [...selectedList.filter(key => !columnKeysSet.has(key)), ...columnSelection];
};

/** Selects (or deselects) all the `shownKeys` keeping the rest of the selection. */
export const toggleShownSelection = (selectedList: string[], shownKeys: string[], select: boolean): string[] =>
  select ? [...new Set([...selectedList, ...shownKeys])] : mergeColumnSelection(selectedList, shownKeys, []);

/** Splits a bulk text into trimmed, non-empty entries (separated by commas or new lines). */
export const parseBulkEntries = (text?: string): string[] =>
  (text ?? '')
    .split(/[,\n]+/)
    .map(item => item.trim())
    .filter(item => item !== '');
